import React, { useEffect, useState } from "react";
import "../../styles/layout.css";
import { useActiveSection, sectionConfig } from "../../hooks/useActiveSection";
import { cn } from "../../lib/utils";

function Navbar() {
  const [open, setOpen] = useState(false);
  const activeSection = useActiveSection();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? "hidden" : prev || "";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) setOpen(false);
    };
    document.addEventListener("keydown", onKey, { passive: true });
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const scrollToSection = (id) => {
    const navHeight = 64;
    const el = document.getElementById(id);
    if (el) {
      const top =
        el.getBoundingClientRect().top + window.pageYOffset - navHeight;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleNavClick = (e, id) => {
    if (e && e.preventDefault) e.preventDefault();
    scrollToSection(id);
    if (open) setOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 h-16 flex items-center justify-between gap-4 px-4 border-b border-black/10 backdrop-blur-sm font-display bg-hero-background">
      {/* Brand */}
      <a
        className="font-bold text-xl tracking-widest text-hero-foreground whitespace-nowrap hover:opacity-80 transition-opacity"
        href="#hero"
        onClick={(e) => handleNavClick(e, "hero")}
      >
        &lt;/&gt; JACK AGUILERA
      </a>

      <div className="flex-1 flex items-center justify-end">
        {/* Mobile current section */}
        <span
          className={cn(
            "lg:hidden font-display text-base font-semibold",
            sectionConfig[activeSection]?.textClass || "text-hero-foreground"
          )}
          aria-label="Open navigation menu"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          {sectionConfig[activeSection]?.label}
        </span>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-8">
          {[
            { id: "experience", href: "#experience", label: "Experience" },
            { id: "projects", href: "#projects", label: "Projects" },
            { id: "about", href: "#about", label: "About" },
            { id: "contact", href: "#contact", label: "Contact" },
          ].map((item) => {
            const isActive = activeSection === item.id;
            const colorMap = {
              experience: "text-orange-600",
              projects: "text-blue-600",
              about: "text-purple-600",
              contact: "text-green-600",
            };
            const gradientMap = {
              experience: "from-orange-600 via-orange-500 to-orange-400",
              projects: "from-blue-600 via-blue-500 to-blue-400",
              about: "from-purple-600 via-purple-500 to-purple-400",
              contact: "from-green-600 via-green-500 to-green-400",
            };
            return (
              <a
                key={item.id}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.id)}
                className={cn(
                  "relative font-body text-sm transition-all duration-300",
                  isActive
                    ? `${colorMap[item.id]} font-medium`
                    : `text-hero-foreground/70 hover:${colorMap[item.id]}`
                )}
              >
                {item.label}
                {/* Active underline */}
                {isActive && (
                  <span
                    className={cn(
                      "absolute left-0 -bottom-1 h-0.5 w-full bg-gradient-to-r",
                      gradientMap[item.id]
                    )}
                  ></span>
                )}
              </a>
            );
          })}
        </div>
      </div>

      {/* Mobile Nav Sheet */}
      {open && (
        <div
          className="fixed inset-x-0 top-36 bottom-0 z-50 lg:hidden bg-hero-background/95 backdrop-blur-sm flex items-center justify-center"
          role="dialog"
          aria-label="Mobile navigation"
          aria-modal="true"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-11/12 max-w-sm rounded-lg border border-black/10 bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <span
                className="font-display text-sm"
                style={{
                  color: sectionConfig[activeSection]?.color || "inherit",
                }}
              >
                {sectionConfig[activeSection]?.label}
              </span>
              <button
                className="px-2 py-1 text-sm rounded border border-black/10 hover:bg-gray-100"
                aria-label="Close navigation menu"
                onClick={() => setOpen(false)}
              >
                Close
              </button>
            </div>
            <nav className="flex flex-col gap-3">
              {[
                { id: "about", label: sectionConfig["about"].label },
                { id: "experience", label: sectionConfig["experience"].label },
                { id: "projects", label: sectionConfig["projects"].label },
                { id: "contact", label: sectionConfig["contact"].label },
              ].map((item) => (
                <button
                  key={item.id}
                  className={cn(
                    "text-left px-3 py-2 rounded-md transition-colors",
                    activeSection === item.id
                      ? "bg-gray-100 text-primary"
                      : "hover:bg-gray-50 text-gray-800"
                  )}
                  onClick={(e) => handleNavClick(e, item.id)}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
